import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { CmpApiService, CmpConfigDto, CmpConfigProviderDto, CmpPurgeResultDto } from '../../services/cmp-api.service';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { ProvidersApiService } from '../../services/providers-api.service';
import { CmpFormComponent } from './form/cmp-form.component';
import { CMP_SNIPPET_TEMPLATE } from './cmp-snippet-template';

@Component({
  selector: 'app-cmp',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, CmpFormComponent],
  templateUrl: './cmp.component.html',
  styleUrls: ['./cmp.component.scss']
})
export class CmpComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  @ViewChild(CmpFormComponent) cmpFormComponent?: CmpFormComponent;

  configs: CmpConfigDto[] = [];
  filteredConfigs: CmpConfigDto[] = [];

  isLoading = false;
  loadingError: string | null = null;
  searchTerm = '';
  pageSizeOptions = [10, 25, 50];
  pageSize = 10;
  currentPage = 0;
  panelOpen = false;
  formMode: 'create' | 'edit' = 'create';
  isDeploying = new Set<number>();
  isPurging = new Set<number>();

  activeActionMenuId: number | null = null;
  activeActionItem: CmpConfigDto | null = null;
  actionMenuPosition = { top: 0, left: 0 };
  actionMenuArrowOffset = 24;
  providerNameMap: Record<string, string> = {};
  currentCoreVersion = '1.0.0';
  availableCoreVersions: string[] = ['1.0.0'];

  private parseSemver(v: string): [number, number, number] {
    const p = (v || '1.0.0').split('.').map(Number);
    return [p[0] || 0, p[1] || 0, p[2] || 0];
  }

  semverOutdated(deployed: string | null, current: string): boolean {
    if (!deployed) return true;
    const [ma, mi, pa] = this.parseSemver(deployed);
    const [ca, ci, cp] = this.parseSemver(current);
    return ca > ma || ci > mi || cp > pa;
  }

  semverMajorBump(deployed: string | null, current: string): boolean {
    if (!deployed) return false;
    return this.parseSemver(current)[0] > this.parseSemver(deployed)[0];
  }

  snippetModal: { providerName: string; providerID: string; script: string; gcsUrl: string | null } | null = null;
  snippetCopied = false;
  private snippetCopyTimer: ReturnType<typeof setTimeout> | null = null;

  readonly locale = 'it-IT';
  readonly Math = Math;

  constructor(
    private cmpApi: CmpApiService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private providersApi: ProvidersApiService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadProviderNames();
    this.authService.waitForEntitySync().subscribe(() => this.loadConfigs());

    const dcrSub = this.authService.selectedDcr$.pipe(
      distinctUntilChanged(),
      skip(1),
      switchMap(() => this.authService.waitForEntitySync())
    ).subscribe(() => {
      this.panelOpen = false;
      this.loadConfigs();
    });
    this.subscriptions.add(dcrSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get paginatedConfigs(): CmpConfigDto[] {
    const start = this.currentPage * this.pageSize;
    return this.filteredConfigs.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredConfigs.length / this.pageSize);
  }

  private async loadProviderNames(): Promise<void> {
    try {
      const providers = await firstValueFrom(this.providersApi.getProviders());
      this.providerNameMap = {};
      for (const p of providers) {
        this.providerNameMap[p.ID] = p.Name || p.ID;
      }
    } catch {}
  }

  async loadConfigs(): Promise<void> {
    this.isLoading = true;
    this.loadingError = null;
    try {
      this.configs = await firstValueFrom(this.cmpApi.getConfigs());
      if (this.configs.length) {
        this.currentCoreVersion = this.configs[0].CurrentCoreVersion ?? '1.0.0';
        this.availableCoreVersions = this.configs[0].AvailableCoreVersions ?? ['1.0.0'];
      }
      this.applyFilter();
    } catch {
      this.loadingError = await firstValueFrom(this.translate.get('cmp.errors.loadFailed'));
    } finally {
      this.isLoading = false;
    }
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.currentPage = 0;
    this.applyFilter();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 0;
    this.applyFilter();
  }

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredConfigs = term
      ? this.configs.filter(c => c.Name.toLowerCase().includes(term))
      : [...this.configs];
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 0;
  }

  prevPage(): void {
    if (this.currentPage > 0) this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.currentPage++;
  }

  openCreatePanel(): void {
    const usedProviderIds = this.configs.flatMap(c => c.Providers.map(p => p.ProviderID));
    this.formMode = 'create';
    this.panelOpen = true;
    setTimeout(() => this.cmpFormComponent?.openCreate(usedProviderIds), 0);
  }

  openEditPanel(config: CmpConfigDto): void {
    const usedProviderIds = this.configs
      .filter(c => c.ID !== config.ID)
      .flatMap(c => c.Providers.map(p => p.ProviderID));
    this.formMode = 'edit';
    this.panelOpen = true;
    setTimeout(() => this.cmpFormComponent?.openEdit(config, usedProviderIds), 0);
  }

  closePanel(): void {
    this.panelOpen = false;
  }

  onFormSaved(): void {
    this.panelOpen = false;
    this.loadConfigs();
  }

  async deleteConfig(config: CmpConfigDto): Promise<void> {
    const msg = await firstValueFrom(this.translate.get('cmp.actions.confirmDelete', { name: config.Name }));
    if (!confirm(msg)) return;
    const deployedProviders = config.Providers.filter(p => p.DeployStatus === 'deployed');
    if (deployedProviders.length > 0) {
      const fileList = deployedProviders.map(p => {
        const name = this.providerNameMap[p.ProviderID] || p.ProviderID;
        return `  • ${name}  (${p.ProviderID}/cmp.js)`;
      }).join('\n');
      const msg2 = await firstValueFrom(this.translate.get('cmp.actions.confirmDeleteGcs', { files: fileList }));
      if (!confirm(msg2)) return;
    }
    try {
      await firstValueFrom(this.cmpApi.deleteConfig(config.ID));
      this.configs = this.configs.filter(c => c.ID !== config.ID);
      this.applyFilter();
      this.notificationService.notify('success',
        await firstValueFrom(this.translate.get('cmp.notifications.deleted'))
      );
    } catch {
      this.notificationService.notify('error',
        await firstValueFrom(this.translate.get('cmp.errors.deleteFailed'))
      );
    }
  }

  async deployConfig(config: CmpConfigDto): Promise<void> {
    if (this.isDeploying.has(config.ID)) return;
    this.isDeploying.add(config.ID);
    this.closeActionMenu();
    try {
      const result = await firstValueFrom(this.cmpApi.deploy(config.ID));
      await this.loadConfigs();
      if (result.errors.length > 0) {
        this.notificationService.notify('error',
          await firstValueFrom(this.translate.get('cmp.notifications.deployPartial', {
            ok: result.deployed.length,
            err: result.errors.length
          }))
        );
      } else {
        this.notificationService.notify('success',
          await firstValueFrom(this.translate.get('cmp.notifications.deployed', { count: result.deployed.length }))
        );
      }
    } catch {
      this.notificationService.notify('error',
        await firstValueFrom(this.translate.get('cmp.errors.deployFailed'))
      );
    } finally {
      this.isDeploying.delete(config.ID);
    }
  }

  async purgeCdn(config: CmpConfigDto): Promise<void> {
    if (this.isPurging.has(config.ID)) return;
    this.isPurging.add(config.ID);
    this.closeActionMenu();
    try {
      const result: CmpPurgeResultDto = await firstValueFrom(this.cmpApi.purge(config.ID));
      if (result.skipped) {
        this.notificationService.notify('info',
          await firstValueFrom(this.translate.get('cmp.purge.skipped'))
        );
      } else if (result.errors && result.errors.length > 0) {
        this.notificationService.notify('warning',
          await firstValueFrom(this.translate.get('cmp.purge.partialError', { errors: result.errors.length }))
        );
      } else {
        this.notificationService.notify('success',
          await firstValueFrom(this.translate.get('cmp.purge.success', { count: result.invalidated.length }))
        );
      }
    } catch {
      this.notificationService.notify('error',
        await firstValueFrom(this.translate.get('cmp.purge.error'))
      );
    } finally {
      this.isPurging.delete(config.ID);
    }
  }

  openActionMenu(event: MouseEvent, config: CmpConfigDto): void {
    event.stopPropagation();
    if (this.activeActionMenuId === config.ID) {
      this.closeActionMenu();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.actionMenuPosition = {
      top: rect.bottom + window.scrollY + 4,
      left: rect.right + window.scrollX - 180
    };
    this.actionMenuArrowOffset = rect.left + rect.width / 2 - this.actionMenuPosition.left;
    this.activeActionMenuId = config.ID;
    this.activeActionItem = config;
  }

  closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.activeActionItem = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeActionMenu();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.snippetModal) { this.closeSnippetModal(); return; }
    this.closeActionMenu();
  }

  openSnippetModal(event: MouseEvent, config: CmpConfigDto, provider: CmpConfigProviderDto): void {
    event.stopPropagation();
    const providerName = this.providerNameMap[provider.ProviderID] || provider.ProviderID;
    const script = CMP_SNIPPET_TEMPLATE
      .replace(/\{\{PROVIDER_NAME\}\}/g, providerName)
      .replace(/\{\{PROVIDER_ID\}\}/g, provider.ProviderID)
      .replace(/\{\{CMP_JS_URL\}\}/g, provider.GcsUrl || `[deploy cmp.js per ${provider.ProviderID} prima]`);
    this.snippetModal = { providerName, providerID: provider.ProviderID, script, gcsUrl: provider.GcsUrl || null };
  }

  closeSnippetModal(): void {
    this.snippetModal = null;
    this.snippetCopied = false;
    if (this.snippetCopyTimer) { clearTimeout(this.snippetCopyTimer); this.snippetCopyTimer = null; }
  }

  copySnippet(): void {
    if (!this.snippetModal) return;
    navigator.clipboard.writeText(this.snippetModal.script).then(() => {
      this.snippetCopied = true;
      this.notificationService.notify('success', this.translate.instant('cmp.snippet.copied'));
      if (this.snippetCopyTimer) clearTimeout(this.snippetCopyTimer);
      this.snippetCopyTimer = setTimeout(() => { this.snippetCopied = false; }, 2500);
    });
  }

  downloadSnippet(): void {
    if (!this.snippetModal) return;
    const safeName = this.snippetModal.providerName.replace(/[^a-zA-Z0-9_\-]/g, '_');
    const filename = `cmp_${safeName}.html.txt`;
    const blob = new Blob([this.snippetModal.script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  getProviderName(providerId: string): string {
    return this.providerNameMap[providerId] || providerId;
  }

  getDeployStatusClass(status: string): string {
    return {
      deployed: 'deploy-badge--deployed',
      error: 'deploy-badge--error',
      pending: 'deploy-badge--pending'
    }[status] ?? 'deploy-badge--pending';
  }

  isProviderOutdated(config: CmpConfigDto, provider: CmpConfigProviderDto): boolean {
    if (provider.DeployStatus !== 'deployed') return false;
    return this.semverOutdated(provider.CoreVersion, config.CurrentCoreVersion);
  }

  getDeployStatusLabel(status: string): string {
    return { deployed: 'Deployed', error: 'Error', pending: 'Pending' }[status] ?? status;
  }

  getVendorsLabel(config: CmpConfigDto): string {
    const vendors = Array.isArray(config.Vendors) ? config.Vendors : [];
    if (!vendors.length) return '—';
    if (vendors.length <= 3) return vendors.map(v => v.name).join(', ');
    return `${vendors.slice(0, 2).map(v => v.name).join(', ')} +${vendors.length - 2}`;
  }
}
